import React from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { AvatarLarge, Button } from '../../components';
import ScoreBadge from '../../components/ScoreBadge/ScoreBadge';
import { useMatchingMatrix } from '../../hooks/useMatchingMatrix';
import routeConfiguration from '../../routeConfiguration';
import { pathByRouteName } from '../../util/routes';

import { getTimeDiffRepresentation } from '../../util/time';
import { createSlug } from '../../util/urlHelpers';
import config from '../../config';
import css from './VisitorCard.css';
import {
    anonymizeUser,
    checkIfListingIsAnonymous,
    checkIfUserIsAnonymous,
    shouldListingBeDeanonymizedForUser,
    shouldUserBeDeanonymizedForUser,
} from '../../util/anonym';
import { IconAnonymousL } from '../../icons';

const { userTypeRider } = config;
/**
 * when a horseowners is visited
 *
 * visitedEntryId - listingId
 * visitorRepresentationId - riderId
 */

/**
 * when a rider is visited
 *
 * visitedEntryId - riderId
 * visitorRepresentationId - mainHorseId
 */

export default ({
    visitorRepresentationId,
    visitedEntryId,
    visitedAt,
    unlocked,
    handleLockedCardClick,
    currentListing,
    avatarEntry,
    riderListing,
    currentUser,
}) => {
    const history = useHistory();
    const entryAuthor = avatarEntry?.author;

    const {
        protectedData: { representationListingId },
        publicData: { userType },
    } = currentUser.attributes.profile;

    const currentUserIsRider = userType === userTypeRider;

    const { days, hours, minutes } = getTimeDiffRepresentation(new Date(visitedAt), new Date(), {
        daysLongText: 'Tagen',
    });

    const matchingMatrixRiderParams = {
        riderListingId: representationListingId,
        horseOwnerListingId: visitorRepresentationId /** mainHorseId */,
    };

    const matchingMatrixOwnersParams = {
        riderListingId: riderListing && riderListing.id ? riderListing.id.uuid : null,
        horseOwnerListingId: visitedEntryId /** listingId */,
    };

    /* useEffect */
    const [matchingMatrixLoading, matchingScore] = useMatchingMatrix(
        currentUserIsRider ? matchingMatrixRiderParams : matchingMatrixOwnersParams
    );

    const showScore = matchingScore && matchingScore.finalScore;
    const isAnonymousListing =
        currentUserIsRider &&
        checkIfListingIsAnonymous(currentListing) &&
        !shouldListingBeDeanonymizedForUser(currentListing, currentUser);

    const isAnonymousUser =
        !currentUserIsRider &&
        checkIfUserIsAnonymous(entryAuthor) &&
        !shouldUserBeDeanonymizedForUser(currentUser, entryAuthor);

    const author = isAnonymousListing || isAnonymousUser ? anonymizeUser(entryAuthor) : entryAuthor;

    const { mainListingImage } = currentListing?.attributes?.publicData || {};

    const handleListingAvatarClick = e => {
        if (isAnonymousListing) return;

        e.stopPropagation();

        const slug = createSlug(currentListing);
        const params = { id: currentListing.id.uuid, slug };
        const pathname = pathByRouteName('ListingPage', routeConfiguration(), params);
        history.push(pathname);
    };

    const handleUnlockedCardClick = () => {
        if (isAnonymousListing) return;

        const params = {
            id: author.id.uuid,
            userType: author.attributes.profile.publicData.userType,
        };
        const pathname = pathByRouteName('ProfilePage', routeConfiguration(), params);
        history.push(pathname);
    };

    return matchingMatrixLoading ? null : (
        <div
            className={classNames(css.wrapper, {
                [css.locked]: !unlocked,
            })}
        >
            {!unlocked && (
                <div className={css.lockedCardOverlay}>
                    <Button
                        type="button"
                        onClick={() => {
                            handleLockedCardClick({
                                visitorRepresentationId,
                                visitedEntryId,
                            });
                        }}
                    >
                        Ansicht aufdecken
                    </Button>
                </div>
            )}
            <div
                className={css.container}
                onClick={() => {
                    if (unlocked) handleUnlockedCardClick();
                }}
            >
                <div
                    className={classNames(css.imagesHolder, {
                        [css.locked]: !unlocked,
                    })}
                >
                    {author && (
                        <AvatarLarge
                            className={css.avatar}
                            user={author}
                            disableProfileLink
                            avatarType="default"
                        />
                    )}

                    {mainListingImage && (
                        <div
                            className={classNames(css.listingImageHolder, {
                                [css.anonym]: isAnonymousListing,
                            })}
                        >
                            <div
                                className={css.listingImage}
                                onClick={handleListingAvatarClick}
                                style={{ backgroundImage: `url(${mainListingImage})` }}
                            />
                            <IconAnonymousL />
                        </div>
                    )}
                </div>
                {showScore ? (
                    <div className={css.matchingContainer}>
                        <ScoreBadge
                            scoreData={{ maxScore: 100, total: matchingScore.finalScore }}
                            scorePercentageText={`${matchingScore.finalScore}% Übereinstimmung`}
                        />
                    </div>
                ) : (
                    <div className={css.matchingContainerPlaceholder} />
                )}

                <span className={css.visitedAt}>
                    Profil angesehen・Vor {days || hours || minutes || ''}
                </span>
            </div>
        </div>
    );
};
